import { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Edit } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import type { ForgotPasswordFormData } from '../../types/auth';
import { PASSWORD_REGEX, stripNonDigits } from '../../utils/validators';
import { authAPI } from '../../services/auth-api.service';
import { envService } from '../../services/env.service';

interface ForgotPasswordFormProps {
  onBack?: () => void;
  onOTPSent?: (email: string) => void;
  onPasswordReset?: () => void;
  onShowToast?: (message: string) => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onOTPSent,
  onPasswordReset,
  onShowToast,
}) => {
  const {
    isLoading,
    error,
    goToLogin,
    clearAuthError,
    validateEmail,
  } = useAuth();

  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: '',
  });

  const [fieldError, setFieldError] = useState<string>('');

  // New state for the multi-step flow
  const [currentStep, setCurrentStep] = useState<'email' | 'otp' | 'password'>('email');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getErrorMessage = (error: any, fallback: string) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };

  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => timer - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  // Handle sending OTP via React API
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = validateEmail(formData.email);
    if (emailError) {
      setFieldError(emailError);
      return;
    }

    setFieldError('');

    try {
      const response = await authAPI.sendForgotPasswordOTP(formData.email);

      const refId =
        response.data?.data?.reference_id ||
        (response.data as any)?.reference_id ||
        '';

      if (!refId) {
        throw new Error('No reference id returned from server');
      }

      console.log('React: OTP sent successfully', {
        referenceId: refId,
        status: response.data?.status,
      });

      setReferenceId(refId);
      setCurrentStep('otp');
      setResendTimer(10); // Start 10-second timer
      onOTPSent?.(formData.email);
      onShowToast?.(`An OTP has been sent to ${formData.email}`);
    } catch (error: any) {
      console.error('React: Failed to send OTP', error);
      setFieldError(getErrorMessage(error, 'Failed to send OTP. Please try again.'));
    }
  };

  // Handle resend OTP via React API
  const handleResendOTP = async () => {
    try {
      const response = await authAPI.sendForgotPasswordOTP(formData.email);

      const refId =
        response.data?.data?.reference_id ||
        (response.data as any)?.reference_id ||
        referenceId;

      if (refId) {
        setReferenceId(refId);
      }

      console.log('React: OTP resent successfully', {
        status: response.data?.status,
        referenceId: refId,
      });
      setResendTimer(10); // Restart 10-second timer
      setOtpError('');
      onShowToast?.(`An OTP has been sent to ${formData.email}`);
    } catch (error: any) {
      console.error('React: Failed to resend OTP', error);
      setOtpError(getErrorMessage(error, 'Failed to resend OTP. Please try again.'));
    }
  };

  // Handle OTP verification via React API
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    if (!referenceId) {
      setOtpError('Please enter the OTP sent to your email.');
      return;
    }

    setOtpError('');

    try {
      const response = await authAPI.verifyForgotPasswordOTP({
        reference_id: referenceId,
        otp,
      });

      // Security Fix: Comprehensive validation matching Angular implementation

      // Validation 1: Check response structure
      if (!response || !response.data || !response.data.data) {
        setOtpError('Something went wrong. Please try again.');
        return;
      }

      // Validation 2: Extract and check token exists and is not empty
      const token = response.data.data.token || '';

      if (!token || token.trim() === '') {
        setOtpError('Something went wrong. Please try again.');
        return;
      }

      // Validation 3: JWT format validation (should have 3 parts separated by dots)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        setOtpError('Something went wrong. Please try again.');
        return;
      }

      // Validation 4: Check response status field
      const status = response.data.status;
      if (status && status !== 'success') {
        const message = response.data.message || 'OTP verification failed';
        setOtpError(message);
        return;
      }

      // Validation 5: Verify reference_id matches (prevents replay attacks)
      const responseReferenceId = response.data.data.reference_id;
      if (responseReferenceId && responseReferenceId !== referenceId) {
        setOtpError('Something went wrong. Please try again.');
        return;
      }

      // All validations passed - safe to proceed
      sessionStorage.setItem('finpro_k_Authorization', token);

      console.log('React: OTP verified successfully', {
        hasToken: !!token,
        tokenLength: token.length,
      });

      setCurrentStep('password');
    } catch (error: any) {
      console.error('React: Failed to verify OTP', error);
      setOtpError(getErrorMessage(error, 'OTP verification failed. Please try again.'));
    }
  };

  // Handle password reset via React API
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    // Password validation - matching Angular's pattern
    const pwdmin = Number(sessionStorage.getItem('pwdmin') || 8);
    const pwdmax = Number(sessionStorage.getItem('pwdmax') || 30);

    if (!newPassword || newPassword.length < pwdmin) {
      setPasswordError(`Password must be at least ${pwdmin} characters long`);
      return;
    }

    if (newPassword.length > pwdmax) {
      setPasswordError(`Password must be at most ${pwdmax} characters long`);
      return;
    }

    if (!PASSWORD_REGEX.test(newPassword)) {
      setPasswordError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match. Please check and try again.');
      return;
    }

    setPasswordError('');

    try {
      const changePasswordPayload = {
        email: formData.email,
        newpassword: newPassword,
        oldpassword: envService.forgotPasswordPlaceholder,
        reference_id: referenceId, // Security: Add reference_id for audit trail
      };

      const response = await authAPI.resetPassword(changePasswordPayload);

      console.log('React: Password reset successfully', {
        status: response.data?.status,
        message: response.data?.message,
      });

      onShowToast?.('Password changed successfully');
      onPasswordReset?.();

      // Security: Clean up temporary token after successful reset
      sessionStorage.removeItem('finpro_k_Authorization');

      // Delay navigation to allow toast to be visible
      setTimeout(() => {
        goToLogin();
      }, 1000);
    } catch (error: any) {
      console.error('React: Failed to reset password', error);
      setPasswordError(getErrorMessage(error, 'Password reset failed. Please try again.'));
    }
  };

  const handleEmailChange = useCallback((email: string) => {
    setFormData({ email });
    if (fieldError) setFieldError('');
    if (error) clearAuthError();
  }, [fieldError, error, clearAuthError]);

  // Step 1: Email Input
  if (currentStep === 'email') {
    return (
      <div className="w-full h-full flex flex-col min-h-0">
        {/* Subtitle */}
        <p className="text-gray-600 text-sm mb-4">
          You will receive an OTP in your registered email
        </p>

        {/* Form - Full Height */}
        <div className="flex-1 flex flex-col min-h-0">
          <form onSubmit={handleSendOTP} className="flex-1 flex flex-col">
            {/* Form Fields Section - Top */}
            <div className="space-y-6 mt-8">
              <FormField
                label="EMAIL ADDRESS"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                error={fieldError}
                placeholder="Enter your email address"
                autoComplete="email"
                required
              />

              {/* Error Message - Plain Text Below Email Field */}
              {error && (
                <div className="text-sm" style={{ color: '#CA7373' }}>
                  {error}
                </div>
              )}
            </div>

            {/* Spacer - pushes button to bottom */}
            <div className="flex-1"></div>

            {/* Button Section - Absolute Bottom */}
            <div className="pb-4">
              <Button
                type="submit"
                loading={isLoading}
                disabled={!formData.email}
                fullWidth
                size="lg"
                variant="gradient"
              >
                Send OTP
              </Button>

              {/* Back to Login Link */}
              <div className="text-center text-sm" style={{ marginTop: '1rem' }}>
                <span
                  onClick={goToLogin}
                  className="font-medium cursor-pointer hover:underline"
                  style={{ color: '#0074A5' }}
                >
                  Back to Login
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Step 2: OTP Verification (matching Angular)
  if (currentStep === 'otp') {
    return (
      <div className="w-full h-full flex flex-col min-h-0">
        {/* Header */}
        <div className=" mb-6">
        <div className="flex items-center gap-2">
          <p className="text-black font-medium m-0">
            Please enter the 6-digit OTP sent to <span style={{ fontWeight: 'bold', color: '#000000' }}>{formData?.email}</span>
          </p>
          <button
            type="button"
            onClick={() => setCurrentStep('email')}
            className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              padding: '2px',
              cursor: 'pointer'
            }}
            aria-label="Edit email"
          >
            <Edit className="w-4 h-4" />
          </button>
        </div>
        </div>

        {/* Form - Full Height */}
        <div className="flex-1 flex flex-col min-h-0">
          <form onSubmit={handleVerifyOTP} className="flex-1 flex flex-col">
            {/* Form Fields Section - Top */}
            <div className="space-y-6 mt-8">
              <div>
                <label htmlFor="otp" className="block uppercase mb-2" style={{
                  color: '#0F3340',
                  fontFamily: 'Helvetica Neue, sans-serif',
                  fontWeight: 500,
                  fontSize: '1.125rem',
                  lineHeight: '100%',
                  letterSpacing: '0.02em'
                }}>
                  OTP
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = stripNonDigits(e.target.value).slice(0, 6);
                    setOtp(value);
                    if (otpError) setOtpError('');
                  }}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="w-full pl-3 pr-4 py-3 text-left text-lg tracking-wider rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1B84C6]"
                  style={{
                    border: '1px solid #D9F0FF'
                  }}
                  autoComplete="one-time-code"
                  required
                />
                {/* Resend Timer/Link - Below input, right-aligned */}
                <div className="mt-2 text-right text-xs">
                  {resendTimer > 0 ? (
                    <span className="text-gray-500 whitespace-nowrap">
                      Resend in {resendTimer}s
                    </span>
                  ) : (
                    <span
                      onClick={handleResendOTP}
                      className="text-black underline cursor-pointer hover:text-gray-700 whitespace-nowrap"
                    >
                      RESEND OTP
                    </span>
                  )}
                </div>
              </div>

              {/* Error Message - Plain Text Below OTP Field */}
              {otpError && (
                <div className="text-sm" style={{ color: '#CA7373' }}>
                  {otpError}
                </div>
              )}
            </div>

            {/* Spacer - pushes button to bottom */}
            <div className="flex-1"></div>

            {/* Button Section - Absolute Bottom */}
            <div className="pb-4">
              <Button
                type="submit"
                loading={isLoading}
                disabled={otp.length !== 6}
                fullWidth
                size="lg"
                variant="gradient"
              >
                Verify Code
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Step 3: Password Reset (matching Angular)
  if (currentStep === 'password') {
    return (
      <div className="w-full h-full flex flex-col min-h-0">
        {/* Header */}
        <div className="mb-6">
          <p className="text-gray-600">
            Enter your new password
          </p>
        </div>

        {/* Form - Full Height */}
        <div className="flex-1 flex flex-col min-h-0">
          <form onSubmit={handlePasswordReset} className="flex-1 flex flex-col">
            {/* Form Fields Section - Top */}
            <div className="space-y-6 mt-8">
              <div>
                <label htmlFor="newPassword" className="block uppercase mb-2" style={{
                  color: '#0F3340',
                  fontFamily: 'Helvetica Neue, sans-serif',
                  fontWeight: 500,
                  fontSize: '1.125rem',
                  lineHeight: '100%',
                  letterSpacing: '0.02em'
                }}>
                  NEW PASSWORD
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (passwordError) setPasswordError('');
                    }}
                    placeholder="Enter new password"
                    className={`
                      w-full pl-3 ${newPassword ? 'pr-12' : 'pr-4'} py-3 rounded-lg transition-colors bg-white
                      ${passwordError
                        ? 'focus:outline-none focus:ring-2 focus:ring-red-500'
                        : 'focus:outline-none focus:ring-2 focus:ring-[#1B84C6]'}
                    `}
                    style={{
                      border: passwordError ? '1px solid #fca5a5' : '1px solid #D9F0FF'
                    }}
                    required
                  />
                  {newPassword && (
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      style={{
                        right: '12px',
                        background: 'none',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        padding: '4px',
                        cursor: 'pointer',
                        zIndex: 10
                      }}
                    >
                      {showNewPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block uppercase mb-2 mt-4" style={{
                  color: '#0F3340',
                  fontFamily: 'Helvetica Neue, sans-serif',
                  fontWeight: 500,
                  fontSize: '1.125rem',
                  lineHeight: '100%',
                  letterSpacing: '0.02em'
                }}>
                  CONFIRM PASSWORD
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (passwordError) setPasswordError('');
                    }}
                    placeholder="Confirm new password"
                    className={`
                      w-full pl-3 ${confirmPassword ? 'pr-12' : 'pr-4'} py-3 rounded-lg transition-colors bg-white
                      ${passwordError
                        ? 'focus:outline-none focus:ring-2 focus:ring-red-500'
                        : 'focus:outline-none focus:ring-2 focus:ring-[#1B84C6]'}
                    `}
                    style={{
                      border: passwordError ? '1px solid #fca5a5' : '1px solid #D9F0FF'
                    }}
                    required
                  />
                  {confirmPassword && (
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      style={{
                        right: '12px',
                        background: 'none',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        padding: '4px',
                        cursor: 'pointer',
                        zIndex: 10
                      }}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Error Message - Plain Text Below Confirm Password Field */}
              {passwordError && (
                <div className="text-sm" style={{ color: '#CA7373' }}>
                  {passwordError}
                </div>
              )}
            </div>

            {/* Spacer - pushes button to bottom */}
            <div className="flex-1"></div>

            {/* Button Section - Absolute Bottom */}
            <div className="pb-4">
              <Button
                type="submit"
                loading={isLoading}
                disabled={!newPassword || !confirmPassword}
                fullWidth
                size="lg"
                variant="gradient"
              >
                Reset Password
              </Button>

              {/* Back to Login Link */}
              <div className="text-center text-sm" style={{ marginTop: '1rem' }}>
                <span
                  onClick={goToLogin}
                  className="font-medium cursor-pointer hover:underline"
                  style={{ color: '#0074A5' }}
                >
                  Back to Login
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
};
